import React, { useState } from 'react'
import { Button, Snackbar, Tooltip } from '@mui/material'
import ShareIcon from '@mui/icons-material/Share'
import { FormattedMessage } from 'react-intl'

interface ShareVacancyProps {
  orderId: string
}

/**
 * Generates a URL for a vacancy page
 * @param orderId The order/task ID
 * @returns Full URL to the vacancy page
 */
export const getVacancyUrl = (orderId: string): string => {
  const baseUrl = window.location.origin

  return `${baseUrl}/vacancies/${orderId}`
}

/**
 * Generates a URL for a vacancy page and copies it to clipboard
 * @param orderId The order/task ID
 * @returns Promise that resolves when the text is copied to clipboard
 */
export const copyVacancyUrlToClipboard = async (orderId: string): Promise<void> => {
  const url = getVacancyUrl(orderId)

  try {
    await navigator.clipboard.writeText(url)
    return await Promise.resolve()
  } catch (err) {
    console.error('Failed to copy vacancy URL to clipboard:', err)
    return Promise.reject(err)
  }
}

const ShareVacancy: React.FC<ShareVacancyProps> = ({ orderId }) => {
  const [open, setOpen] = useState(false)

  const handleClick = async () => {
    try {
      await copyVacancyUrlToClipboard(orderId)
      setOpen(true)
    } catch (error) {
      console.error('Failed to copy vacancy URL:', error)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      <Tooltip
        title={<FormattedMessage id="share-vacancy-link" defaultMessage="Share vacancy link" />}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ShareIcon />}
          onClick={handleClick}
          sx={{ ml: 1 }}
        >
          <FormattedMessage id="share-vacancy" defaultMessage="Share Vacancy" />
        </Button>
      </Tooltip>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        message={
          <FormattedMessage
            id="vacancy-url-copied"
            defaultMessage="Vacancy URL copied to clipboard"
          />
        }
      />
    </>
  )
}

export default ShareVacancy
